import { useDisclosure } from '@chakra-ui/react';
import { ChangeEvent, useState } from 'react';

import { Prefecture } from '@/domain/models/Store';
import {
  AuthStoreFormError,
  validateBuilding,
  validateCity,
  validateConfirmPassword,
  validateEmail,
  validateLatitude,
  validateLongitude,
  validateName,
  validatePassword,
  validatePhoneNumber,
  validatePrefecture,
  validateStreetAddress,
  validateZipCode,
} from '@/pages/store-management/auth-stores/components/AuthStoreSidebarForm/validate';
import { GeoLocation } from '@/pages/store-management/auth-stores/service';
import { StoreManagementAuthStoreShowReqponse } from '@/services/StoreManagement/AuthStores/hooks';
import {
  fetchAddressByPosCode,
  ZipCodeResult,
} from '@/services/StoreManagement/PostCode/fetchAddressByPosCode';

type Return = {
  isOpenAddressList: boolean;
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
  addFeatureIds: number[];
  phoneNumber: string;
  zipCode: string;
  prefecture: Prefecture | '';
  addresses: ZipCodeResult[];
  city: string;
  streetAddress: string;
  building: string;
  geoLocation: GeoLocation;
  initialGeoLocation: GeoLocation;
  isLoading: boolean;
  initialStringifyFormState: string;
  error: AuthStoreFormError;
  setError: React.Dispatch<React.SetStateAction<AuthStoreFormError>>;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setInitialStringifyFormState: React.Dispatch<React.SetStateAction<string>>;
  handleInputName: (e: ChangeEvent<HTMLInputElement>) => void;
  handleInputEmail: (e: ChangeEvent<HTMLInputElement>) => void;
  handleInputPassword: (e: ChangeEvent<HTMLInputElement>) => void;
  handleInputConfirmPassword: (e: ChangeEvent<HTMLInputElement>) => void;
  handleChangeAddFeatures: (flag: Flag, targetAddFeatureId: number) => void;
  handleInputPhoneNumber: (e: ChangeEvent<HTMLInputElement>) => void;
  handleInputZipCode: (e: ChangeEvent<HTMLInputElement>) => void;
  onCloseAddressList: () => void;
  handleSearchAdressByPosCode: () => Promise<void>;
  handleSetAddress: (index: number) => void;
  handleInputCity: (e: ChangeEvent<HTMLInputElement>) => void;
  handleChangePrefecture: (e: ChangeEvent<HTMLSelectElement>) => void;
  handleInputStreetAddress: (e: ChangeEvent<HTMLInputElement>) => void;
  handleInputBuilding: (e: ChangeEvent<HTMLInputElement>) => void;
  handleInputLatitude: (e: ChangeEvent<HTMLInputElement>) => void;
  handleInputLongitude: (e: ChangeEvent<HTMLInputElement>) => void;
  initializeEditState: (
    targetAuthStore: StoreManagementAuthStoreShowReqponse
  ) => Promise<void>;
  initializeForm: () => void;
};
const ON = 'ON';
const OFF = 'OFF';
type Flag = typeof ON | typeof OFF;

export const useStoreManagementAuthStoreForm = (): Return => {
  const [name, setName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [zipCode, setZipCode] = useState<string>('');
  const [prefecture, setPrefecture] = useState<Prefecture | ''>('');
  const [city, setCity] = useState<string>('');
  const [streetAddress, setStreetAddress] = useState<string>('');
  const [building, setBuilding] = useState<string>('');
  const [geoLocation, setGeoLocation] = useState<GeoLocation>({
    latitude: null,
    longitude: null,
  });
  const [initialGeoLocation, setInitialGeoLocation] = useState<GeoLocation>({
    latitude: null,
    longitude: null,
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [addresses, setAddresses] = useState<ZipCodeResult[]>([]);
  const [addFeatureIds, setAddFeatureIds] = useState<number[]>([]);
  const {
    isOpen: isOpenAddressList,
    onOpen: onOpenAddressList,
    onClose: onCloseAddressList,
  } = useDisclosure();
  const errorObj = { message: '', isError: false, isTouched: false };
  const initialErrorObj = {
    name: { ...errorObj },
    email: { ...errorObj },
    password: { ...errorObj },
    confirmPassword: { ...errorObj },
    phoneNumber: { ...errorObj },
    prefecture: { ...errorObj },
    city: { ...errorObj },
    streetAddress: { ...errorObj },
    building: { ...errorObj },
    zipCode: { ...errorObj },
    latitude: { ...errorObj },
    longitude: { ...errorObj },
  };
  const [error, setError] = useState<AuthStoreFormError>({
    ...initialErrorObj,
  });
  const [initialStringifyFormState, setInitialStringifyFormState] =
    useState<string>('');

  const handleInputName = (e: ChangeEvent<HTMLInputElement>) => {
    const newName = e.target.value;
    const newError = validateName(newName, error);
    setName(newName), setError(newError);
  };
  const handleInputEmail = (e: ChangeEvent<HTMLInputElement>) => {
    const newEmail = e.target.value;
    const newError = validateEmail(newEmail, error);
    setEmail(newEmail), setError(newError);
  };
  const handleInputPassword = (e: ChangeEvent<HTMLInputElement>) => {
    const newPassword = e.target.value;
    const newError = validatePassword(newPassword, error);
    setPassword(newPassword), setError(newError);
  };
  const handleInputConfirmPassword = (e: ChangeEvent<HTMLInputElement>) => {
    const newConfirmPassword = e.target.value;
    const newError = validateConfirmPassword(newConfirmPassword, error);
    setConfirmPassword(newConfirmPassword), setError(newError);
  };
  const handleChangeAddFeatures = (flag: Flag, targetAddFeatureId: number) => {
    if (flag === 'ON') {
      setAddFeatureIds((prev) => [...prev, targetAddFeatureId]);
    } else {
      setAddFeatureIds((prev) =>
        prev.filter((af) => af !== targetAddFeatureId)
      );
    }
  };
  const handleInputPhoneNumber = (e: ChangeEvent<HTMLInputElement>) => {
    const newPhoneNumber = e.target.value;
    const newError = validatePhoneNumber(newPhoneNumber, error);
    setPhoneNumber(newPhoneNumber), setError(newError);
  };
  const handleInputZipCode = (e: ChangeEvent<HTMLInputElement>) => {
    const newZipCode = e.target.value;
    const newError = validateZipCode(newZipCode, error);
    setZipCode(newZipCode), setError(newError);
  };
  const handleChangePrefecture = (e: ChangeEvent<HTMLSelectElement>) => {
    const newPrefecture = e.target.value as Prefecture;
    const newError = validatePrefecture(newPrefecture, error);
    setPrefecture(newPrefecture), setError(newError);
  };
  const handleInputCity = (e: ChangeEvent<HTMLInputElement>) => {
    const newCity = e.target.value;
    const newError = validateCity(newCity, error);
    setCity(newCity), setError(newError);
  };
  const handleInputStreetAddress = (e: ChangeEvent<HTMLInputElement>) => {
    const newStreetAddress = e.target.value;
    const newError = validateStreetAddress(newStreetAddress, error);
    setStreetAddress(newStreetAddress), setError(newError);
  };
  const handleInputBuilding = (e: ChangeEvent<HTMLInputElement>) => {
    const newBuilding = e.target.value;
    const newError = validateBuilding(newBuilding, error);
    setBuilding(newBuilding), setError(newError);
  };
  const coordinatePattern = /^([0-9-.]*)$/; // 半角数字とマイナス記号を許容する正規表現
  const handleInputLatitude = (e: ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    if (!coordinatePattern.test(newValue)) return;
    const newError = validateLatitude(+newValue, error);
    setError(newError);
    setGeoLocation({ ...geoLocation, latitude: +newValue });
  };
  const handleInputLongitude = (e: ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    if (!coordinatePattern.test(newValue)) return;
    const newError = validateLongitude(+newValue, error);
    setError(newError);
    setGeoLocation({ ...geoLocation, longitude: +newValue });
  };
  const setAddress = (address: ZipCodeResult) => {
    setPrefecture(address.address1 as Prefecture);
    setCity(address.address2);
    setStreetAddress(address.address3);
    const newError = validatePrefecture(address.address1, error);
    const newError2 = validateCity(address.address2, newError);
    const newError3 = validateStreetAddress(address.address3, newError2);
    setError(newError3);
  };
  const handleSetAddress = (index: number) => {
    const findedAdress = addresses.find((_, _index) => _index === index);
    if (findedAdress === undefined) return;
    setAddress(findedAdress), onCloseAddressList();
  };
  const handleSearchAdressByPosCode = async () => {
    setIsLoading(true);
    const res = await fetchAddressByPosCode(zipCode);
    if (res.message === null && res.results === null)
      return alert('住所検索に失敗しました。'), setIsLoading(false);
    if (res.status === 400) return alert(res.message), setIsLoading(false);
    if (res.results.length > 1) {
      return (
        setAddresses(res.results), onOpenAddressList(), setIsLoading(false)
      );
    }
    setAddress(res.results[0]), setIsLoading(false);
  };

  const initializeForm = () => {
    setError({ ...initialErrorObj });
    setName(''),
      setEmail(''),
      setPassword(''),
      setConfirmPassword(''),
      setPhoneNumber('');
    setPrefecture(''), setCity('');
    setStreetAddress(''), setBuilding('');
    setZipCode(''), setAddFeatureIds([]);
    setGeoLocation({ latitude: null, longitude: null });
  };

  const initializeEditState = async (
    targetAuthStore: StoreManagementAuthStoreShowReqponse
  ) => {
    if (targetAuthStore === undefined) return;
    setError({ ...initialErrorObj });
    setIsLoading(true);
    const {
      name,
      email,
      phone_number,
      prefecture,
      city,
      street_address,
      building,
      zip_code,
      add_features,
      latitude,
      longitude,
    } = targetAuthStore;
    const newBuilding = building ?? '';
    const addFeatureIds = add_features.map((af) => af.id);
    setName(name), setEmail(email), setPhoneNumber(phone_number);
    setPrefecture(prefecture), setCity(city);
    setStreetAddress(street_address), setBuilding(newBuilding);
    setZipCode(zip_code), setAddFeatureIds(addFeatureIds);
    setGeoLocation({ latitude: latitude, longitude: longitude });
    setInitialGeoLocation({
      latitude: latitude,
      longitude: longitude,
    });
    setInitialStringifyFormState(
      JSON.stringify([
        name,
        email,
        phone_number,
        prefecture,
        city,
        street_address,
        newBuilding,
        zip_code,
        addFeatureIds,
        {
          latitude: latitude,
          longitude: longitude,
        } as GeoLocation,
      ])
    );
    setIsLoading(false);
  };

  return {
    isOpenAddressList,
    name,
    email,
    password,
    confirmPassword,
    addFeatureIds,
    phoneNumber,
    zipCode,
    prefecture,
    addresses,
    city,
    streetAddress,
    building,
    geoLocation,
    initialGeoLocation,
    isLoading,
    error,
    initialStringifyFormState,
    setError,
    setIsLoading,
    setInitialStringifyFormState,
    handleInputName,
    handleInputEmail,
    handleInputPassword,
    handleInputConfirmPassword,
    handleChangeAddFeatures,
    handleInputPhoneNumber,
    handleInputZipCode,
    onCloseAddressList,
    handleSearchAdressByPosCode,
    handleSetAddress,
    handleInputCity,
    handleChangePrefecture,
    handleInputStreetAddress,
    handleInputBuilding,
    handleInputLatitude,
    handleInputLongitude,
    initializeEditState,
    initializeForm,
  };
};
